<template>
  <div class="app-container">
    <BjPage
      ref="BjPage"
      :label-width="'100px'"
      :query-params="queryParams"
      :col-span="colSpan"
      :get-action="queryApi"
      :serial="serial"
      :selection="selection"
      :before-reset-search="beforeResetSearch"
      @handleSelectionChange="handleSelectionChange"
      @showSetting="SortableTable.show"
      @header-dragend="SortableTable.handleHeaderDragend"
      @header-contextmenu="SortableTable.handleHeaderContextmenu"
    >
      <template v-slot:baseForm>
        <el-col :span="colSpan">
          <el-form-item :label="$t('QuotationCustomerLang.Carrier')" prop="logisticsCompanyId">
            <BjSelectLov v-model="queryParams.logisticsCompanyId" :option-list="carrierList" :placeholder="$t('QuotationCustomerLang.Carrier')" @change="handleQuery" />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('QuotationCustomerLang.EffectiveDateRange')" prop="effectiveTime">
            <BjDateRangePicker v-model="queryEffectiveTime" :tip-show="true" :placeholder="$t('QuotationCustomerLang.EffectiveDateRange')" @change="handleQuery" />
          </el-form-item>
        </el-col>
      </template>

      <template v-slot:tableOperation="tableOperationProp">
        <el-col>
          <el-button type="primary" plain @click="onAdd">{{ $t('QuotationCustomerLang.AddRate') }}</el-button>
          <el-button type="primary" plain :disabled="tableOperationProp.notSelected || hasSelectedEnabledStatus" @click="onBatchEnable">{{ $t('QuotationCustomerLang.BatchEnable') }}</el-button>
          <el-button type="primary" plain :disabled="tableOperationProp.notSelected || hasSelectedDisabledStatus" @click="onBatchDisable">{{ $t('QuotationCustomerLang.BatchDisable') }}</el-button>
          <el-button type="primary" plain :disabled="tableOperationProp.notSelected" @click="onDelete">{{ $t('bulkDelText') }}</el-button>
          <el-button type="primary" plain @click="onDownloadImportTemplate">{{ $t('QuotationCustomerLang.ImportTemplate') }}</el-button>
          <el-button type="primary" plain @click="onImport">{{ $t('QuotationCustomerLang.Import') }}</el-button>
          <BjFrontendExport
            type="primary"
            plain
            :label-list="labelList"
            :get-rows="getRateExportRows"
            :file-name="$t('QuotationCustomerLang.FuelSurchargeRateFileName')"
            translate-label
          >
            {{ $t('QuotationCustomerLang.Export') }}
          </BjFrontendExport>
        </el-col>
      </template>

      <template v-slot:tableColumn>
        <template v-for="(col, index) in dropCols">
          <el-table-column
            v-if="col.prop !== 'bjTableAction'"
            :key="`col_${index}_${col.prop}_${col.width || 'auto'}`"
            :prop="col.prop"
            :label="col.label"
            :width="col.width || 'auto'"
            :fixed="col.fixed || false"
            align="center"
            class-name="item"
          >
            <template slot-scope="scope">
              <template v-if="col.prop === 'surchargeRate'">
                <span class="rate-text">{{ formatRate(scope.row[col.prop]) }}</span>
              </template>
              <template v-else-if="col.prop === 'effectiveFrom' || col.prop === 'effectiveTo'">
                {{ scope.row[col.prop] | timeFilters({ dateFormat: 'DD/MM/YYYY' }) }}
              </template>
              <template v-else-if="col.prop === 'status'">
                <el-tag :type="scope.row[col.prop] == 1 ? 'success' : 'info'">
                  {{ $t(STATUS_ENABLED_NUM.bjGet(scope.row[col.prop])) }}
                </el-tag>
              </template>
              <template v-else>{{ scope.row[col.prop] | timeFilters(scope.row[col.prop]) }}</template>
            </template>
          </el-table-column>
          <el-table-column
            v-if="col.prop === 'bjTableAction'"
            :key="`col_${index}_${col.prop}_${col.width || 'auto'}`"
            :prop="col.prop"
            :label="col.label"
            :width="col.width || 'auto'"
            :fixed="col.fixed || 'right'"
            align="center"
          >
            <template slot-scope="props">
              <el-button type="text" class="edit-btn" @click="onEdit(props.row)">{{ $t('editText') }}</el-button>
            </template>
          </el-table-column>
        </template>
      </template>
    </BjPage>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogFormVisible"
      width="760px"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-position="top" class="quotation-rate-form">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item :label="$t('QuotationCustomerLang.RateNo')" prop="rateCode">
              <BjInput v-model="form.rateCode" :placeholder="$t('QuotationCustomerLang.RateNo')" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('QuotationCustomerLang.Carrier')" prop="logisticsCompanyId">
              <BjSelectLov v-model="form.logisticsCompanyId" :option-list="carrierList" :placeholder="$t('QuotationCustomerLang.Carrier')" @change="handleCarrierChange" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('QuotationCustomerLang.SurchargeRatePercent')" prop="surchargeRate">
              <BjInputMoney v-model="form.surchargeRate" :min="1" :max="100" :precision="2" :placeholder="$t('QuotationCustomerLang.SurchargeRatePercent')" clearable not-auto-zero>
                <template #append>%</template>
              </BjInputMoney>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('QuotationCustomerLang.Status')" prop="status">
              <BjSelectLov v-model="form.status" :option-list="STATUS_ENABLED_NUM" :placeholder="$t('QuotationCustomerLang.Status')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('QuotationCustomerLang.EffectiveDateRange')" prop="effectiveTime">
              <BjDateRangePicker v-model="formEffectiveTime" :tip-show="true" :placeholder="$t('QuotationCustomerLang.EffectiveDateRange')" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('QuotationCustomerLang.Remarks')" prop="remark">
              <BjInput
                v-model="form.remark"
                type="textarea"
                :rows="5"
                maxlength="500"
                show-word-limit
                :placeholder="$t('QuotationCustomerLang.Remarks')"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleClose">{{ $t('cancelText') }}</el-button>
        <el-button type="primary" @click="saveRate">{{ $t('okText') }}</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="$t('QuotationCustomerLang.Import')"
      :visible.sync="importDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @closed="resetImportDialog"
    >
      <el-form ref="importForm" :model="importForm" :rules="importRules" label-position="top" class="quotation-rate-import-form">
        <el-form-item :label="$t('QuotationCustomerLang.Carrier')" prop="logisticsCompanyId">
          <BjSelectLov v-model="importForm.logisticsCompanyId" :option-list="carrierList" :placeholder="$t('QuotationCustomerLang.Carrier')" />
        </el-form-item>
        <el-form-item :label="$t('QuotationCustomerLang.ImportFile')" prop="file">
          <el-upload
            ref="importUpload"
            class="import-upload"
            drag
            action=""
            accept=".xls,.xlsx"
            :limit="1"
            :file-list="importFileList"
            :auto-upload="false"
            :on-change="handleImportFileChange"
            :on-exceed="handleImportFileExceed"
            :on-remove="handleImportFileRemove"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">{{ $t('UploadMainText1') }}<em>{{ $t('UploadMainText2') }}</em></div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="importDialogVisible = false">{{ $t('cancelText') }}</el-button>
        <el-button type="primary" @click="onConfirmImport">{{ $t('okText') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { logisticApi } from '@/api';
import { STATUS_ENABLED_NUM } from '@/utils/constants';
import pageMiXin from '@/utils/page-mixin.js';

const FUEL_SURCHARGE_RATE_IMPORT_TEMPLATE_URL = 'https://minio.wooper.com/test/11/FuelSurchargeRateImportTemplate';

const getDefaultForm = () => ({
  id: null,
  rateCode: null,
  logisticsCompanyId: null,
  logisticsCompanyName: null,
  surchargeRate: null,
  status: 1,
  effectiveFrom: null,
  effectiveTo: null,
  remark: null,
});

export default {
  name: 'BusinessFuelSurchargeRate',
  mixins: [pageMiXin],
  data() {
    const validateEffectiveTime = (rule, value, callback) => {
      if (!this.form.effectiveFrom || !this.form.effectiveTo) {
        callback(new Error(this.$t('chooseText')));
        return;
      }
      callback();
    };

    return {
      STATUS_ENABLED_NUM,
      colSpan: 4,
      serial: true,
      selection: true,
      queryApi: logisticApi.getFuelSurcharges,
      queryEffectiveTime: [],
      formEffectiveTime: [],
      queryParams: {
        logisticsCompanyId: null,
        effectiveFrom: null,
        effectiveTo: null,
      },
      labelList: [
        { label: 'QuotationCustomerLang.RateNo', prop: 'rateCode', isCheck: true, disabled: false },
        { label: 'QuotationCustomerLang.Carrier', prop: 'logisticsCompanyName', isCheck: true, disabled: false },
        { label: 'QuotationCustomerLang.SurchargeRatePercent', prop: 'surchargeRate', isCheck: true, disabled: false, export: { formatter: value => this.formatRate(value) } },
        { label: 'QuotationCustomerLang.StartTime', prop: 'effectiveFrom', isCheck: true, disabled: false, export: { dateFormat: 'DD/MM/YYYY' } },
        { label: 'QuotationCustomerLang.EndTime', prop: 'effectiveTo', isCheck: true, disabled: false, export: { dateFormat: 'DD/MM/YYYY' } },
        { label: 'QuotationCustomerLang.Status', prop: 'status', isCheck: true, disabled: false, export: { options: STATUS_ENABLED_NUM, translate: true } },
        { label: 'QuotationCustomerLang.Operation', prop: 'bjTableAction', isCheck: true, disabled: false, width: 120 },
      ],
      carrierList: [],

      dialogFormVisible: false,
      importDialogVisible: false,
      dialogTitle: this.$t('QuotationCustomerLang.FuelSurchargeRate'),
      form: getDefaultForm(),
      importForm: {
        logisticsCompanyId: null,
        file: null,
      },
      importFileList: [],
      rules: {
        logisticsCompanyId: [{ required: true, message: this.$t('chooseText'), trigger: 'change' }],
        surchargeRate: [{ required: true, message: this.$t('inputText'), trigger: 'blur' }],
        status: [{ required: true, message: this.$t('chooseText'), trigger: 'change' }],
        effectiveTime: [{ required: true, validator: validateEffectiveTime, trigger: 'change' }],
      },
      importRules: {
        logisticsCompanyId: [{ required: true, message: this.$t('chooseText'), trigger: 'change' }],
        file: [{ required: true, message: this.$t('chooseText'), trigger: 'change' }],
      },
    };
  },
  computed: {
    hasSelectedEnabledStatus() {
      return this.selectedRows.some(item => item.status == 1);
    },
    hasSelectedDisabledStatus() {
      return this.selectedRows.some(item => item.status == 0);
    },
  },
  watch: {
    queryEffectiveTime(value) {
      this.queryParams.effectiveFrom = (value || [])[0] || null;
      this.queryParams.effectiveTo = (value || [])[1] || null;
    },
    formEffectiveTime(value) {
      this.form.effectiveFrom = (value || [])[0] || null;
      this.form.effectiveTo = (value || [])[1] || null;
    },
  },
  mounted() {
    this.getCarrierList();
  },
  methods: {
    getCarrierList() {
      logisticApi.getLogisticsCompany({}).then(res => {
        this.carrierList = (res.content || []).map(item => ({
          label: item.name,
          value: item.id,
          name: item.name,
        }));
      });
    },
    handleCarrierChange(value, option) {
      const selected = option || this.carrierList.find(item => String(item.value) === String(value));
      this.form.logisticsCompanyName = selected ? selected.name || selected.label : null;
    },
    formatRate(value) {
      if (value === null || value === undefined || value === '') return '';
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? `${value}%` : `${numberValue.toFixed(2)}%`;
    },
    onAdd() {
      this.dialogTitle = this.$t('QuotationCustomerLang.FuelSurchargeRate');
      this.form = getDefaultForm();
      this.formEffectiveTime = [];
      this.dialogFormVisible = true;
      this.clearFormValidate();
    },
    onEdit(row) {
      this.dialogTitle = this.$t('QuotationCustomerLang.FuelSurchargeRate');
      const data = JSON.parse(JSON.stringify(row || {}));
      this.form = Object.assign(getDefaultForm(), data);
      this.formEffectiveTime = this.form.effectiveFrom && this.form.effectiveTo ? [this.form.effectiveFrom, this.form.effectiveTo] : [];
      this.dialogFormVisible = true;
      this.clearFormValidate();
    },
    getSelectedIds() {
      return this.selectedRows.map(item => item.id).filter(Boolean);
    },
    onDelete() {
      const ids = this.getSelectedIds();
      this.$confirm(this.$t('toast.delAllTipText'), this.$t('toast.hintText'), {
        confirmButtonText: this.$t('toast.okText'),
        cancelButtonText: this.$t('toast.cancelText'),
        type: 'warning',
      })
        .then(() => {
          logisticApi.deleteFuelSurcharge({ ids }).then(res => {
            if (!res.failed) {
              this.msgSuccess(this.$t('toast.operationSuccessText'));
              this.reFresh();
            }
          });
        })
        .catch(() => {});
    },
    onBatchEnable() {
      this.changeStatus(1);
    },
    onBatchDisable() {
      this.changeStatus(0);
    },
    changeStatus(status) {
      const ids = this.getSelectedIds();
      logisticApi.updateFuelSurchargeStatus({ ids, status }).then(res => {
        if (!res.failed) {
          this.msgSuccess(this.$t('toast.operationSuccessText'));
          this.reFresh();
        }
      });
    },
    onDownloadImportTemplate() {
      const download = document.createElement('a');
      download.href = FUEL_SURCHARGE_RATE_IMPORT_TEMPLATE_URL;
      download.download = 'FuelSurchargeRateImportTemplate';
      document.body.appendChild(download);
      download.click();
      document.body.removeChild(download);
    },
    onImport() {
      this.importDialogVisible = true;
    },
    handleImportFileChange(file, fileList) {
      this.importFileList = fileList.slice(-1);
      this.importForm.file = file && file.raw;
      this.$refs.importForm && this.$refs.importForm.validateField('file');
    },
    handleImportFileExceed(files) {
      const rawFile = files && files[0];
      this.importFileList = rawFile ? [{ name: rawFile.name, raw: rawFile }] : [];
      this.importForm.file = rawFile || null;
      this.$refs.importForm && this.$refs.importForm.validateField('file');
    },
    handleImportFileRemove() {
      this.importFileList = [];
      this.importForm.file = null;
    },
    getRateExportRows() {
      return this.selectedRows.length
        ? this.selectedRows
        : (this.$refs.BjPage ? this.$refs.BjPage.tableData : []);
    },
    buildFuelSurchargePayload() {
      const payload = {
        effectiveFrom: this.form.effectiveFrom,
        effectiveTo: this.form.effectiveTo,
        logisticsCompanyId: this.form.logisticsCompanyId,
        logisticsCompanyName: this.form.logisticsCompanyName,
        rateCode: this.form.rateCode,
        remark: this.form.remark,
        status: this.form.status,
        surchargeRate: this.form.surchargeRate,
      };
      if (this.form.id) {
        payload.id = this.form.id;
      }
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
          delete payload[key];
        }
      });
      return payload;
    },
    saveRate() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.$scrollToFormError && this.$scrollToFormError();
          return;
        }
        const action = this.form.id ? logisticApi.updateFuelSurcharge : logisticApi.addFuelSurcharge;
        const payload = this.buildFuelSurchargePayload();
        action(payload).then(res => {
          if (!res.failed) {
            this.msgSuccess(this.$t('toast.operationSuccessText'));
            this.handleClose();
            this.reFresh();
          }
        });
      });
    },
    onConfirmImport() {
      this.$refs.importForm.validate(valid => {
        if (!valid) return;
        const formData = new FormData();
        formData.append('file', this.importForm.file);
        logisticApi.importFuelSurcharges({
          BjQuery: { logisticsCompanyId: this.importForm.logisticsCompanyId },
          BjBody: formData,
        }).then(res => {
          if (!res.failed) {
            this.msgSuccess(this.$t('toast.operationSuccessText'));
            this.importDialogVisible = false;
            this.reFresh();
          }
        });
      });
    },
    resetImportDialog() {
      this.importForm = {
        logisticsCompanyId: null,
        file: null,
      };
      this.importFileList = [];
      this.$nextTick(() => {
        this.$refs.importForm && this.$refs.importForm.clearValidate();
        this.$refs.importUpload && this.$refs.importUpload.clearFiles();
      });
    },
    handleClose() {
      this.dialogFormVisible = false;
      this.form = getDefaultForm();
      this.formEffectiveTime = [];
      this.clearFormValidate();
    },
    clearFormValidate() {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    beforeResetSearch() {
      this.queryEffectiveTime = [];
      this.handleSelectionChange({});
      return Promise.resolve();
    },
  },
};
</script>

<style lang="scss" scoped>
.quotation-rate-form {
  padding: 0 4px;
}

.rate-text {
  color: #a35f20;
  font-weight: 600;
}

::v-deep .quotation-rate-form .el-select,
::v-deep .quotation-rate-form .el-input,
::v-deep .quotation-rate-form .el-date-editor {
  width: 100%;
}

::v-deep .quotation-rate-import-form .el-select,
::v-deep .quotation-rate-import-form .el-input {
  width: 100%;
}

::v-deep .quotation-rate-import-form .import-upload,
::v-deep .quotation-rate-import-form .import-upload .el-upload,
::v-deep .quotation-rate-import-form .import-upload .el-upload-dragger {
  display: block;
  width: 100%;
}
</style>
